import { observer } from "mobx-react";
import React from "react";
import { Divider, Grid, Popover, Typography, useTheme } from "@mui/material";
import { ContentCopy, HealthAndSafety, InfoRounded } from "@mui/icons-material";
import copy from "copy-to-clipboard";
import { AcxTranscriptionStore } from "../Stores/AcxTranscriptionStore";
import ScissorsSvg from "SvgIcons/ScissorsSvg";
import AcxButton from "components/UI/AcxButton";
import { durationDisplay } from "components/SoundClipEditor/Controls/Player";

type Props = {
    store: AcxTranscriptionStore;
    onCreateClip: () => void;
    onRedact: () => void;
    disableClip?: boolean;
    disableRedact?: boolean;
};

const AcxClipPopover = observer(
    ({ store, onCreateClip, onRedact, disableClip = false, disableRedact = false}: Props) => {
        const [anchorElement, setAnchorElement] =
            React.useState<HTMLElement | null>(null);
        const theme = useTheme();

        React.useEffect(() => {
            let anchor: HTMLElement | null = null;
            if (
                typeof store.selectedStartTimestamp === "number" &&
                typeof store.selectedEndTimestamp === "number"
            ) {
                const startSpan = document.querySelector(
                    `span[id^='${store.selectedStartTimestamp}-']`,
                ) as HTMLElement | null;
                const endSpan = document.querySelector(
                    `span[id$='-${store.selectedEndTimestamp}']`,
                ) as HTMLElement | null;
                // Try to find the blurb container as a fallback
                const blurbContainer = document.getElementById(
                    `blurb-${store.selectedStartTimestamp}-${store.selectedEndTimestamp}-blurb`,
                ) as HTMLElement | null;

                anchor = endSpan || startSpan || blurbContainer;
            }
            setAnchorElement(anchor);
        }, [store.selectedStartTimestamp, store.selectedEndTimestamp]);

        // Only render Popover if selection is finished and timestamps are valid
        if (
            !anchorElement ||
            typeof store.selectedStartTimestamp !== "number" ||
            typeof store.selectedEndTimestamp !== "number" ||
            store.selectedStartTimestamp === store.selectedEndTimestamp ||
            store.selectingViaAudio
        ) {
            return null;
        }

        return (
            <Popover
                open={true}
                anchorEl={anchorElement}
                onClose={() => {
                    store.setSelectedStartTimestamp(null);
                    store.setSelectedEndTimestamp(null);
                }}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: 1,
                            border: `0.5px solid ${theme.palette.gray[200]}`,
                            backgroundColor: `${theme.palette.background.paper}`,
                            boxShadow: `0px 10px 15px 0px rgba(0, 0, 0, 0.10), 0px 4px 6px 0px rgba(0, 0, 0, 0.05)`,
                            p: 1,
                            display: "flex",
                            flexDirection: "column",
                            width: "182px",
                            alignItems: "center",
                            gap: 1,
                        },
                    },
                }}
            >
                <Grid
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        width: "100%",
                        justifyContent: "center",
                    }}
                >
                    <Typography
                        sx={{
                            color: theme.palette.primary.main,
                            fontWeight: theme.typography.fontWeightSemibold,
                            width: "100%",
                            textAlign: "center",
                        }}
                    >
                        {durationDisplay(store.selectedStartTimestamp)} -{" "}
                        {durationDisplay(store.selectedEndTimestamp)}
                    </Typography>
                    {store.overlappingSpeakers && (
                        <Grid
                            sx={{
                                display: "flex",
                                flexDirection: "row",
                                width: "100%",
                                justifyContent: "center",
                                alignItems: "center",
                                gap: 1,
                            }}
                        >
                            <InfoRounded
                                sx={{
                                    color: theme.palette.primary.main,
                                    width: 16,
                                    height: 16,
                                }}
                            />
                            <Typography
                                sx={{
                                    color: theme.palette.primary.main,
                                    fontWeight:
                                        theme.typography.fontWeightRegular,
                                }}
                            >
                                Overlapping Speakers
                            </Typography>
                        </Grid>
                    )}
                </Grid>
                <Divider sx={{ width: "110%" }} />
                {!disableClip && (
                    <Grid
                        sx={{
                            display: "flex",
                            flexDirection: "row",
                            width: "100%",
                        }}
                    >
                        <Grid
                            sx={{
                                flexGrow: 0,
                                display: "flex",
                                alignItems: "center",
                            }}
                        >
                            <ScissorsSvg />
                        </Grid>
                        <Grid
                            sx={{
                                flexGrow: 1,
                                width: "100%",
                                height: "100%",
                                display: "flex",
                                justifyContent: "center",
                            }}
                        >
                            <AcxButton
                                sx={{
                                    color: "#000000 !important",
                                    fontWeight:
                                        theme.typography.fontWeightMedium,
                                    textAlign: "left",
                                }}
                                style={{
                                    display: "flex",
                                    justifyContent: "flex-start",
                                    textAlign: "left",
                                }}
                                fullWidth
                                variant="text"
                                onClick={async (e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    await onCreateClip();
                                    store.setSelectedStartTimestamp(null);
                                    store.setSelectedEndTimestamp(null);
                                }}
                                onMouseUp={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                }}
                            >
                                Create a Clip
                            </AcxButton>
                        </Grid>
                    </Grid>
                )}
                <Grid
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        width: "100%",
                    }}
                >
                    <Grid
                        sx={{
                            flexGrow: 0,
                            display: "flex",
                            alignItems: "center",
                        }}
                    >
                        <ContentCopy />
                    </Grid>
                    <Grid
                        sx={{
                            flexGrow: 1,
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            justifyContent: "center",
                        }}
                    >
                        <AcxButton
                            sx={{
                                color: "#000000 !important",
                                fontWeight: theme.typography.fontWeightMedium,
                                textAlign: "left",
                            }}
                            style={{
                                display: "flex",
                                justifyContent: "flex-start",
                                textAlign: "left",
                            }}
                            fullWidth
                            variant="text"
                            onClick={async (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                await copy(
                                    store.selectedText ??
                                        "Was unable to copy text",
                                );
                                store.setSelectedStartTimestamp(null);
                                store.setSelectedEndTimestamp(null);
                                store.setSelectedEndTimestampTemp(null);
                            }}
                            onMouseUp={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                            }}
                        >
                            Copy
                        </AcxButton>
                    </Grid>
                </Grid>
                {!disableRedact && (
                    <Grid
                        sx={{
                            display: "flex",
                            flexDirection: "row",
                            width: "100%",
                        }}
                    >
                        <Grid
                            sx={{
                                flexGrow: 0,
                                display: "flex",
                                alignItems: "center",
                            }}
                        >
                            <HealthAndSafety />
                        </Grid>
                        <Grid
                            sx={{
                                flexGrow: 1,
                                width: "100%",
                                height: "100%",
                                display: "flex",
                                justifyContent: "center",
                            }}
                        >
                            <AcxButton
                                sx={{
                                    color: "#000000 !important",
                                    fontWeight:
                                        theme.typography.fontWeightMedium,
                                    textAlign: "left",
                                }}
                                style={{
                                    display: "flex",
                                    justifyContent: "flex-start",
                                    textAlign: "left",
                                }}
                                fullWidth
                                variant="text"
                                onClick={async (e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    await onRedact();
                                    store.setSelectedStartTimestamp(null);
                                    store.setSelectedEndTimestamp(null);
                                }}
                                onMouseUp={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                }}
                            >
                                Redact
                            </AcxButton>
                        </Grid>
                    </Grid>
                )}
            </Popover>
        );
    },
);

export default AcxClipPopover;
